/**
 * Outpaint preview geometry.
 *
 * image-apps-v2 zoom_out_percentage is how far the image shrinks inside the
 * original frame: 0 leaves it full size, 50 leaves it at 50% (the API example),
 * and the 0–90 range only makes sense that way. Edge amounts then add pixels
 * outside that frame. Both are sent as themselves — zoom is never copied into
 * expand_*, or the model would apply it twice.
 *
 * A route that cannot scale (local outpaintPrep, flux expand) does not receive
 * a zoom. Local placement is the full image at (left, top) after an 8 px snap.
 */
export interface Size {
  width: number
  height: number
}

export interface Sides {
  left: number
  right: number
  top: number
  bottom: number
}

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface ExpansionLimits {
  maxSide: number
  maxEdge: number
  snap: number
  zoomMin: number
  zoomMax: number
}

export interface ExpansionSpec {
  kind: 'image-apps' | 'edges' | 'bria'
  names: { left: string; right: string; top: string; bottom: string; zoom?: string }
  limits: ExpansionLimits
  canvasKey?: string
  locationKey?: string
  imageSizeKey?: string
}

export interface ExpansionGeometry {
  sides: Sides
  zoom: number
  output: Size
  image: Rect
}

export interface ExpansionSnapshot {
  sides: Sides
  zoom: number
}

export interface ExpansionHistory {
  past: ExpansionSnapshot[]
  present: ExpansionSnapshot
  future: ExpansionSnapshot[]
}

type ParamLike = { name: string; minimum?: number; maximum?: number; default?: unknown }

const EXPAND: Record<keyof Sides, string> = {
  left: 'expand_left',
  right: 'expand_right',
  top: 'expand_top',
  bottom: 'expand_bottom',
}
const ZOOM_KEY = 'zoom_out_percentage'
const ZERO: Sides = { left: 0, right: 0, top: 0, bottom: 0 }

const num = (value: unknown, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

const snapTo = (value: number, step: number) => Math.max(0, Math.round(value / Math.max(1, step)) * Math.max(1, step))

const clampZoom = (zoom: number, limits: ExpansionLimits) =>
  Math.min(limits.zoomMax, Math.max(limits.zoomMin, zoom))

/** 0 → full size, 50 → half size, matching the image-apps-v2 example and range. */
export const imageScaleForZoom = (zoom: number) => 1 - clampZoom(zoom, { zoomMin: 0, zoomMax: 90, maxSide: 0, maxEdge: 0, snap: 1 }) / 100

export function expansionSpec(parameters: readonly ParamLike[]): ExpansionSpec | null {
  const names = new Set(parameters.map(parameter => parameter.name))
  const of = (name: string) => parameters.find(parameter => parameter.name === name)
  if (names.has('canvas_size') && names.has('original_image_location')) {
    return {
      kind: 'bria',
      names: { left: 'expand_left', right: 'expand_right', top: 'expand_top', bottom: 'expand_bottom' },
      canvasKey: 'canvas_size',
      locationKey: 'original_image_location',
      imageSizeKey: names.has('original_image_size') ? 'original_image_size' : undefined,
      limits: { maxSide: 2048, maxEdge: 5000, snap: 1, zoomMin: 0, zoomMax: 0 },
    }
  }
  const sides = (['left', 'right', 'top', 'bottom'] as const).filter(side => names.has(EXPAND[side]))
  if (sides.length === 0 && !names.has(ZOOM_KEY)) return null
  const zoom = names.has(ZOOM_KEY)
  const sideParam = of(EXPAND.left) ?? of(EXPAND.right) ?? of(EXPAND.top) ?? of(EXPAND.bottom)
  const zoomParam = of(ZOOM_KEY)
  return {
    kind: zoom ? 'image-apps' : 'edges',
    names: {
      left: EXPAND.left,
      right: EXPAND.right,
      top: EXPAND.top,
      bottom: EXPAND.bottom,
      zoom: zoom ? ZOOM_KEY : undefined,
    },
    limits: {
      maxSide: num(sideParam?.maximum, zoom ? 700 : 2048),
      maxEdge: 8192,
      snap: 1,
      zoomMin: num(zoomParam?.minimum, 0),
      zoomMax: zoom ? num(zoomParam?.maximum, 90) : 0,
    },
  }
}

export function isExpansionParameter(name: string, spec: ExpansionSpec | null): boolean {
  if (!spec) return false
  return name === spec.names.left || name === spec.names.right || name === spec.names.top || name === spec.names.bottom
    || name === spec.names.zoom || name === spec.canvasKey || name === spec.locationKey || name === spec.imageSizeKey
}

export function limitSides(sides: Sides, natural: Size, limits: ExpansionLimits): Sides {
  const out: Sides = {
    left: Math.min(limits.maxSide, snapTo(sides.left, limits.snap)),
    right: Math.min(limits.maxSide, snapTo(sides.right, limits.snap)),
    top: Math.min(limits.maxSide, snapTo(sides.top, limits.snap)),
    bottom: Math.min(limits.maxSide, snapTo(sides.bottom, limits.snap)),
  }
  const fit = (a: keyof Sides, b: keyof Sides, extent: number) => {
    const room = snapTo(Math.max(0, limits.maxEdge - extent), limits.snap)
    const total = out[a] + out[b]
    if (total <= room) return
    out[a] = snapTo(out[a] * room / Math.max(1, total), limits.snap)
    out[b] = snapTo(out[b] * room / Math.max(1, total), limits.snap)
  }
  fit('left', 'right', natural.width)
  fit('top', 'bottom', natural.height)
  return out
}

/** Where the original sits after zoom-out (inside the source frame) and edge expansion (outside it). */
export function imageAppsPlacement(natural: Size, sides: Sides, zoom: number): ExpansionGeometry & {
  payload: { expand_left: number; expand_right: number; expand_top: number; expand_bottom: number; zoom_out_percentage: number }
} {
  const scale = 1 - Math.min(90, Math.max(0, zoom)) / 100
  const image = {
    width: natural.width * scale,
    height: natural.height * scale,
    x: sides.left + (natural.width - natural.width * scale) / 2,
    y: sides.top + (natural.height - natural.height * scale) / 2,
  }
  return {
    sides,
    zoom,
    output: { width: natural.width + sides.left + sides.right, height: natural.height + sides.top + sides.bottom },
    image,
    payload: {
      expand_left: sides.left,
      expand_right: sides.right,
      expand_top: sides.top,
      expand_bottom: sides.bottom,
      zoom_out_percentage: zoom,
    },
  }
}

/** Local outpaintPrep: full image, 8 px grid, never a hidden zoom scale. */
export function localPlacement(natural: Size, sides: Sides): { payload: Sides; image: Rect; output: Size } {
  const snap8 = (value: number) => Math.max(0, Math.round(value / 8) * 8)
  const limited = limitSides({
    left: snap8(sides.left),
    right: snap8(sides.right),
    top: snap8(sides.top),
    bottom: snap8(sides.bottom),
  }, natural, { maxSide: 1024, maxEdge: 4096, snap: 8, zoomMin: 0, zoomMax: 0 })
  return {
    payload: limited,
    image: { x: limited.left, y: limited.top, width: natural.width, height: natural.height },
    output: {
      width: natural.width + limited.left + limited.right,
      height: natural.height + limited.top + limited.bottom,
    },
  }
}

export function briaPayload(placement: { output: Size; image: Rect }): {
  canvas_size: [number, number]
  original_image_location: [number, number]
  original_image_size: [number, number]
} {
  return {
    canvas_size: [Math.round(placement.output.width), Math.round(placement.output.height)],
    original_image_location: [Math.round(placement.image.x), Math.round(placement.image.y)],
    original_image_size: [Math.round(placement.image.width), Math.round(placement.image.height)],
  }
}

export function readExpansion(spec: ExpansionSpec, settings: Record<string, unknown>, natural: Size): ExpansionGeometry {
  const sides = limitSides({
    left: num(settings[spec.names.left]),
    right: num(settings[spec.names.right]),
    top: num(settings[spec.names.top]),
    bottom: num(settings[spec.names.bottom]),
  }, natural, spec.limits)
  const zoom = spec.names.zoom ? clampZoom(num(settings[spec.names.zoom]), spec.limits) : 0
  const placed = imageAppsPlacement(natural, sides, zoom)
  return { sides, zoom, output: placed.output, image: placed.image }
}

export function writeExpansion(spec: ExpansionSpec, geometry: ExpansionGeometry): Record<string, number | number[]> {
  if (spec.kind === 'bria') {
    const payload = briaPayload(geometry)
    const out: Record<string, number | number[]> = {}
    if (spec.canvasKey) out[spec.canvasKey] = payload.canvas_size
    if (spec.locationKey) out[spec.locationKey] = payload.original_image_location
    if (spec.imageSizeKey) out[spec.imageSizeKey] = payload.original_image_size
    return out
  }
  const out: Record<string, number | number[]> = {
    [spec.names.left]: geometry.sides.left,
    [spec.names.right]: geometry.sides.right,
    [spec.names.top]: geometry.sides.top,
    [spec.names.bottom]: geometry.sides.bottom,
  }
  if (spec.names.zoom) out[spec.names.zoom] = geometry.zoom
  return out
}

const RATIOS: Record<string, number> = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5, '3:2': 3 / 2 }

/** Pad the short axis. Zoom is cleared so it cannot fight the ratio. */
export function sidesForAspect(shape: string, natural: Size, limits: ExpansionLimits): { sides: Sides; zoom: 0 } {
  const ratio = RATIOS[shape]
  if (!ratio) return { sides: ZERO, zoom: 0 }
  const raw: Sides = { ...ZERO }
  if (natural.width / natural.height < ratio) {
    const pad = (natural.height * ratio - natural.width) / 2
    raw.left = pad
    raw.right = pad
  } else {
    const pad = (natural.width / ratio - natural.height) / 2
    raw.top = pad
    raw.bottom = pad
  }
  return { sides: limitSides(raw, natural, limits), zoom: 0 }
}

const OUTWARD: Record<keyof Sides, number> = { left: -1, right: 1, top: -1, bottom: 1 }

export function dragSide(side: keyof Sides, start: Sides, delta: number, pxPerSource: number, natural: Size, limits: ExpansionLimits): Sides {
  const opposite: keyof Sides = side === 'left' ? 'right' : side === 'right' ? 'left' : side === 'top' ? 'bottom' : 'top'
  const extent = side === 'left' || side === 'right' ? natural.width : natural.height
  const room = snapTo(Math.max(0, limits.maxEdge - extent - start[opposite]), limits.snap)
  const raw = start[side] + OUTWARD[side] * delta / Math.max(1e-6, pxPerSource)
  return { ...start, [side]: Math.min(limits.maxSide, room, snapTo(raw, limits.snap)) }
}

const CORNER: Record<string, [keyof Sides, keyof Sides]> = {
  nw: ['left', 'top'],
  ne: ['right', 'top'],
  se: ['right', 'bottom'],
  sw: ['left', 'bottom'],
}

export function dragCorner(corner: string, start: Sides, dx: number, dy: number, pxPerSource: number, natural: Size, limits: ExpansionLimits): Sides {
  const pair = CORNER[corner] ?? CORNER.se
  const afterX = dragSide(pair[0], start, dx, pxPerSource, natural, limits)
  return dragSide(pair[1], afterX, dy, pxPerSource, natural, limits)
}

const sameSnapshot = (a: ExpansionSnapshot, b: ExpansionSnapshot) =>
  a.zoom === b.zoom && a.sides.left === b.sides.left && a.sides.right === b.sides.right && a.sides.top === b.sides.top && a.sides.bottom === b.sides.bottom

export function pushExpansion(history: ExpansionHistory, next: ExpansionSnapshot): ExpansionHistory {
  if (sameSnapshot(history.present, next)) return history
  return { past: [...history.past, history.present], present: next, future: [] }
}

export function undoExpansion(history: ExpansionHistory): ExpansionHistory {
  const present = history.past[history.past.length - 1]
  if (!present) return history
  return { past: history.past.slice(0, -1), present, future: [history.present, ...history.future] }
}

export function redoExpansion(history: ExpansionHistory): ExpansionHistory {
  const present = history.future[0]
  if (!present) return history
  return { past: [...history.past, history.present], present, future: history.future.slice(1) }
}

/** CSS px per output pixel when the expanded canvas is contain-fitted in the stage. */
export function previewScale(output: Size, stage: Size): number {
  return Math.min(stage.width / Math.max(1, output.width), stage.height / Math.max(1, output.height))
}

export const PREVIEW_STAGE: Size = { width: 280, height: 168 }

export function aspectLabel(width: number, height: number): string {
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  let a = w
  let b = h
  while (b) {
    const t = b
    b = a % b
    a = t
  }
  const rw = w / a
  const rh = h / a
  return rw <= 40 && rh <= 40 ? `${rw}:${rh}` : (w / h).toFixed(3)
}
